// Comparison-page data. Every competitor figure was read from the cited source
// on the stated fetch date. Rule: nothing marked unverified in the research doc
// (scratchpad/competitor-data.md) may appear here. Re-fetch cited pages before
// materially editing any number.
export const PRICING_AS_OF = 'August 14, 2026';

export const comparePages = [
  {
    slug: 'smith-ai-alternative',
    name: 'Smith.ai',
    shortTitle: 'UBOTIKA vs Smith.ai',
    title: 'UBOTIKA vs Smith.ai: Flat Minutes vs Per-Call Metering',
    metaTitle: 'Smith.ai Alternative | UBOTIKA vs Smith.ai (2026 Pricing Compared)',
    metaDescription:
      'Smith.ai charges per call — $300/mo for 30 human-answered calls, with appointment booking +$1.50/call and Spanish +$1.00/call. UBOTIKA is a done-for-you AI receptionist with flat plans from $500/mo including 500 minutes. Honest side-by-side.',
    intro:
      'Smith.ai is one of the most established names in call answering, with both human-staffed and AI receptionist lines. The honest difference is the meter: Smith.ai bills per call, with booking and Spanish as per-call surcharges on human plans. UBOTIKA is a Los Angeles agency that builds and runs your AI receptionist for a flat monthly price with a published minute allowance.',
    theirSide: {
      heading: 'Where Smith.ai is genuinely strong',
      points: [
        'A true 24/7 human-staffed option with US-based agents — if you specifically want people, this is the established choice.',
        'No setup fees, no charge for spam calls, and a transparent per-call rate card.',
        'Both AI and human products under one vendor, with a deep integration catalog.',
      ],
    },
    ourSide: {
      heading: 'Where UBOTIKA is different',
      points: [
        'Flat monthly plans with published minutes (500 / 1,500 / 3,500) — a 30-second wrong number doesn\'t burn a $10 call credit.',
        'Appointment booking and bilingual English/Spanish are built into the $899 AI Front Desk plan — on Smith.ai\'s human plans they are +$1.50 and +$1.00 per call.',
        'Done-for-you by a local LA agency: we design the call flows, wire the integrations, and keep tuning from real recordings.',
      ],
    },
    table: [
      ['', 'UBOTIKA', 'Smith.ai — human', 'Smith.ai — AI'],
      ['Entry price', '$500/mo · 500 minutes', '$300/mo · 30 calls', 'Free · 25 calls, then $150/mo Pro'],
      ['Metering', 'Minutes, flat plans', 'Per call (≈$10/call entry; overage $11.50/call)', 'Per call ($1.67–$3.00/call)'],
      ['Appointment booking', 'Included from $899 plan', '+$1.50 per call', 'Included'],
      ['Spanish', 'Included from $899 plan, 24/7', '+$1.00 per call', 'Included'],
      ['Who builds & maintains it', 'UBOTIKA (done-for-you)', 'Their trained staff', 'You (self-serve)'],
      ['Human escalation', 'Warm transfer to your team', 'It is humans', 'Paid per-call add-on'],
      ['Setup fee', '$750+, waived on 12-month term', 'None', 'None'],
    ],
    sources: [
      { label: 'Smith.ai receptionist pricing', url: 'https://smith.ai/pricing/receptionists' },
      { label: 'Smith.ai AI receptionist pricing', url: 'https://smith.ai/ai-receptionist' },
    ],
    faqs: [
      {
        q: 'Is UBOTIKA cheaper than Smith.ai?',
        a: 'It depends on volume and what you count. Smith.ai\'s human line starts at $300/month for 30 calls — about $10 a call, before +$1.50/call booking and +$1.00/call Spanish add-ons. UBOTIKA starts at $500/month with 500 minutes included, and booking plus bilingual coverage are built into the $899 plan rather than metered per call. Low call volume with no add-ons can favor Smith.ai; a busy phone with booking and Spanish usually favors a flat plan.',
      },
      {
        q: 'Smith.ai has real humans — does UBOTIKA?',
        a: 'UBOTIKA\'s receptionist is AI, with warm transfer and escalation rules that hand the calls you specify to your own team. If you want a fully human-staffed desk, Smith.ai is a legitimate choice — you\'ll pay per call for it. Our position: let the AI handle the routine majority around the clock, and let humans handle what genuinely needs them.',
      },
      {
        q: 'What\'s the fastest way to compare them?',
        a: 'Call our live demo line at (424) 434-4541 — an AI answers, because the demo is the product. Then price your last month of calls both ways: your call count times Smith.ai\'s per-call rates (plus add-ons) versus a flat UBOTIKA plan.',
      },
    ],
  },
  {
    slug: 'ruby-receptionist-alternative',
    name: 'Ruby',
    shortTitle: 'UBOTIKA vs Ruby',
    title: 'UBOTIKA vs Ruby: $1.00/min AI vs $5.00/min Humans',
    metaTitle: 'Ruby Receptionist Alternative | UBOTIKA vs Ruby (2026 Pricing)',
    metaDescription:
      'Ruby charges $250/mo for 50 receptionist minutes — $5.00/min — and bilingual answering runs weekdays 5am–6pm PT only. UBOTIKA answers in English and Spanish 24/7 from $500/mo with 500 minutes included. Honest side-by-side.',
    intro:
      'Ruby is the warm-human gold standard in virtual reception, and if a trained person on every call is non-negotiable, it earns its reputation. The trade is arithmetic: $250 a month buys 50 receptionist minutes — five dollars a minute — and by Ruby\'s own FAQ, bilingual answering runs Monday–Friday, 5am–6pm Pacific. UBOTIKA\'s AI answers in English and Spanish at 3am on a Sunday, at an effective $0.80 a minute on the entry plan.',
    theirSide: {
      heading: 'Where Ruby is genuinely strong',
      points: [
        'Real, trained humans answering 24/7/365 — warmth no AI fully matches on delicate calls.',
        'HIPAA-compliant option and a polished mobile app.',
        'Month-to-month with a 21-day money-back guarantee for first-time users.',
      ],
    },
    ourSide: {
      heading: 'Where UBOTIKA is different',
      points: [
        'Effective cost: $500 for 500 minutes (~$1.00/min) vs Ruby\'s $250 for 50 minutes ($5.00/min) — at 500 minutes Ruby\'s published tier is $1,725/month.',
        'Bilingual coverage is 24/7, not weekday business hours — Ruby\'s own FAQ limits bilingual service to Mon–Fri 5am–6pm PT.',
        'Every call answered instantly and simultaneously — no queue when three calls land at once.',
      ],
    },
    table: [
      ['', 'UBOTIKA', 'Ruby'],
      ['Entry price', '$500/mo · 500 minutes (~$1.00/min)', '$250/mo · 50 minutes ($5.00/min)'],
      ['500 minutes costs', '$500', '$1,725 (published 500-min tier)'],
      ['Who answers', 'AI, built and tuned for your business', 'Trained human receptionists'],
      ['Bilingual EN/ES', 'Included from $899 plan, 24/7', 'Available, Mon–Fri 5am–6pm PT (their FAQ)'],
      ['Simultaneous calls', 'Unlimited', 'Subject to staffing'],
      ['Overage', '~$0.25–0.35/min, agreed before signing', 'Per-plan rate, not published on pricing page'],
      ['Contract', 'Month-to-month; setup waived on 12-mo', 'Month-to-month; 21-day money-back (first-time)'],
    ],
    sources: [
      { label: 'Ruby pricing', url: 'https://www.ruby.com/pricing/' },
      { label: 'Ruby FAQ (bilingual hours, billing mechanics)', url: 'https://www.ruby.com/faqs/' },
    ],
    faqs: [
      {
        q: 'Is Ruby worth $5.00 a minute?',
        a: 'For some businesses, yes — a warm human voice on delicate calls has real value, and Ruby\'s people are well trained. The question is whether every routine call (hours, directions, booking, reminders) needs a $5/min human, or whether those should run on a flat AI plan with humans reserved for the calls that genuinely need them.',
      },
      {
        q: 'Does Ruby answer in Spanish around the clock?',
        a: 'No — Ruby\'s own FAQ states bilingual (and outbound) services run Monday through Friday, 5am–6pm Pacific. A Spanish-speaking caller at 8pm or on a Saturday won\'t reach bilingual service. UBOTIKA\'s bilingual call flows run 24/7 because software doesn\'t go home.',
      },
      {
        q: 'Can I try both?',
        a: 'Ruby offers a 21-day money-back guarantee for first-time users. UBOTIKA offers a free missed-call audit, a live demo line — (424) 434-4541, an AI answers — and two weeks free on your own calls. If you continue, the Starter plan is $500/month; the trial itself is free.',
      },
    ],
  },
  {
    slug: 'answering-service-alternative',
    name: 'Answering services',
    shortTitle: 'UBOTIKA vs answering services',
    title: 'AI Receptionist vs Traditional Answering Service: The Real Math',
    metaTitle: 'Answering Service Alternative | AI Receptionist vs $1.50/min Services',
    metaDescription:
      'Traditional answering services meter humans at $1.50–$1.75/minute with overage at 2–3x base rates, holiday surcharges, and booking fees. UBOTIKA replaces the meter with flat AI plans from $500/mo, 500 minutes included.',
    intro:
      'The traditional answering service model is a human call center billing you by the minute — industry sources put basic live answering at $1.50–$1.75 per minute, typical plans at $135–$400 a month, and overage at two to three times the base rate. Most take messages rather than book appointments. The model made sense before software could hold a conversation; the meter is the part that hasn\'t aged well.',
    theirSide: {
      heading: 'Where a traditional service still fits',
      points: [
        'Genuine human voices for practices that require them on every call.',
        'Decades-old vendors with established HIPAA and compliance workflows.',
        'Fine for very low call volume where a small metered bill stays small.',
      ],
    },
    ourSide: {
      heading: 'Why the meter is the problem',
      points: [
        'Per-minute billing punishes growth: a marketing push that doubles your calls doubles your bill — overage often at 2–3x base rates.',
        'Industry sources list the add-ons: setup fees, holiday surcharges, bilingual add-ons, call-transfer fees, appointment-booking fees.',
        'Most services take a message you still have to return. UBOTIKA books the appointment, answers the FAQ, and syncs your CRM on the call itself.',
      ],
    },
    table: [
      ['', 'UBOTIKA', 'Typical answering service'],
      ['Pricing model', 'Flat plans: $500 / $899 / $1,799 with minutes included', '$1.50–$1.75/min metered; plans $135–$400/mo'],
      ['Overage', '~$0.25–0.35/min, agreed up front', '2–3x base per-minute rate (industry sources)'],
      ['Books appointments', 'Yes — on the call, into your calendar', 'Usually a message; booking often a paid add-on'],
      ['Bilingual', 'Included from $899 plan, 24/7', 'Often a paid add-on'],
      ['Holidays', 'Included', 'Holiday surcharges are a documented pattern'],
      ['Simultaneous calls', 'Unlimited', 'Limited by staffing; queues at peak'],
      ['CRM notes', 'Automatic on every call', 'Rare — typically an emailed message'],
    ],
    sources: [
      { label: 'AnswerConnect — call answering service cost (Jul 2025)', url: 'https://www.answerconnect.com/blog/answerconnect-services/call-answering-service-cost/' },
      { label: 'Housecall Pro — answering service cost (May 2026)', url: 'https://www.housecallpro.com/resources/how-much-does-an-answering-service-cost/' },
      { label: 'Ambs Call Center — answering service cost (Jul 2025)', url: 'https://www.ambscallcenter.com/blog/how-much-does-an-answering-service-cost' },
    ],
    faqs: [
      {
        q: 'What does a traditional answering service really cost?',
        a: 'Industry sources — including answering-service providers themselves — put basic live answering at $1.50–$1.75 per minute, with typical small-business plans at $135–$400 a month and overage billed at two to three times the base rate. Documented extras include setup fees, holiday surcharges, bilingual add-ons, and appointment-booking fees, and some vendors bill on 28-day cycles, producing a 13th invoice each year.',
      },
      {
        q: 'What does an answering service actually do with my calls?',
        a: 'Most take a message and relay it — the call-back is still your job. That\'s the structural difference: an AI receptionist resolves the call in the call — answers the question, books the slot, logs the CRM note — instead of queuing work for your morning.',
      },
      {
        q: 'When is an answering service still the right choice?',
        a: 'If your callers must reach a human every time — some clinical and crisis contexts — or your volume is so low a metered bill stays trivial. For the routine majority of business calls, the flat-plan AI model exists precisely because the meter and the message pad aged out.',
      },
    ],
  },
];

export const getComparePage = (slug) => comparePages.find((p) => p.slug === slug);
